import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { getRepromaterialByPartnersReportTemplate } from "../../document-templates/RepromaterialByPartnersReportTemplate";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { RepromaterialByPartnersReport } from "../../models/Report";
import { Partner } from "../../models/Partner";
import { Product } from "../../models/Product";
import { Utility } from "../../core/Utility";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllDocuments } from "../../services/NewVersionService";
import { getAllProducts } from "../../services/ProductService";
import { getAllPartners } from "../../services/PartnerService";
import "./RepromaterialByPartnersReportPage.scss";

export const RepromaterialByPartnersReportPage = () => {
  const { t } = useTranslation();
  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31);

  const getDefaultDateRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      return {
        from: yearStart,
        to: today,
      };
    }
    return {
      from: yearStart,
      to: yearEnd,
    };
  };

  const defaultRange = getDefaultDateRangeForBusinessYear();
  const [dateFrom, setDateFrom] = useState<Date>(defaultRange.from);
  const [dateTo, setDateTo] = useState<Date>(defaultRange.to);
  const [selectedPartner, setSelectedPartner] = useState<string>("");
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [partnersApi, setPartnersApi] = useState<Partner[]>([]);

  const [processing, setProcessing] = useState(false);
  const [repromaterialByPartnersData, setRepromaterialByPartnersData] =
    useState<RepromaterialByPartnersReport[]>([]);
  const [pdfData, setPdfData] = useState<any>(
    getRepromaterialByPartnersReportTemplate(
      repromaterialByPartnersData,
      selectedPartner,
      dateFrom,
      dateTo
    )
  );
  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [pResp, partnerResp]: [AxiosResponse, AxiosResponse] =
      await Promise.all([getAllProducts(), getAllPartners()]);

    [pResp, partnerResp].forEach(ensureSuccess);

    const pr = pResp?.data?.result ?? [];
    const pa = partnerResp?.data?.result ?? [];

    setProductsApi(pr);
    setPartnersApi(pa);

    return { pr, pa };
  };

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        const { pr, pa } = await refreshLookups();
         const data = await buildReportData(pr, pa);
         setRepromaterialByPartnersData(data);
      } finally {
        setProcessing(false);
      }
    };
    run();
  }, []);

  useEffect(() => {
    const range = getDefaultDateRangeForBusinessYear();
    setDateFrom(range.from);
    setDateTo(range.to);
  }, [businessYear]);

  useEffect(() => {
    if (!productsApi.length || !partnersApi.length) return;

    let cancelled = false;
    const run = async () => {
      setProcessing(true);
      try {
        const data = await buildReportData(productsApi, partnersApi);
        if (!cancelled) setRepromaterialByPartnersData(data);
      } finally {
        if (!cancelled) setProcessing(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedPartner, dateFrom, dateTo, productsApi, partnersApi]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getRepromaterialByPartnersReportTemplate(
      repromaterialByPartnersData,
      (partnersApi.find((item) => item.id === selectedPartner)
        ?.name as string) ?? " - Svi",
      dateFrom,
      dateTo
    );
    setPdfData(newPdf);
  }, [
    repromaterialByPartnersData,
    selectedPartner,
    dateFrom,
    dateTo,
    partnersApi,
  ]);

  const buildReportData = async (
    pr: Product[],
    pa: Partner[]
  ): Promise<RepromaterialByPartnersReport[]> => {
    const partner = pa.find((item) => item.id == selectedPartner);
    const partnerName = partner?.name;

    const dateFromValue = dateFrom;
    const dateToExclusive = new Date(
      dateTo.getFullYear(),
      dateTo.getMonth(),
      dateTo.getDate() + 1
    );

    let copyOfRepromaterialByPartnersData: RepromaterialByPartnersReport[] = [];

    // repromaterijal – otpremnice
    const response: AxiosResponse = await getAllDocuments(
      false, // mercantile = false (repromaterijal)
      false, // input = false (otpremnice)
      dateFromValue,
      dateToExclusive
    );

    const docs = response.data?.result ?? [];

    for (const doc of docs) {
      if (doc?.isDocumentFinished !== true) {
        continue;
      }

      // filter po partneru (ako je izabran)
      if (partnerName && doc.partnerName !== partnerName) {
        continue;
      }

      // razlaganje reproProducts / reproAmounts
      const productIds = (doc.reproProducts ?? "")
        .split(";")
        .map((s: string) => s.trim())
        .filter((s: string) => s !== "");

      const amounts = (doc.reproAmounts ?? "")
        .split(";")
        .map((s: string) => s.trim())
        .filter((s: string) => s !== "");

      productIds.forEach((prodId: string, index: number) => {
        const rawAmount = amounts[index] ?? "0";
        const numericAmount = parseFloat(rawAmount); // uzima broj iz npr. "10kg"
        const amount = isNaN(numericAmount) ? 0 : numericAmount;

        const productMeta = pr.find(
          (p) => p.id?.toString() === prodId.toString()
        );
        if (!productMeta) {
          return;
        }

        copyOfRepromaterialByPartnersData.push({
          id: productMeta.id,
          date: new Date(doc.date),
          warehouseName: doc.warehouseName,
          documentNumber:
            doc.finishedDocumentNumber ?? doc.finishedDocumentId ?? "",
          productName: productMeta.name as string,
          amount,
        });
      });
    }

    copyOfRepromaterialByPartnersData.sort(
      (a: RepromaterialByPartnersReport, b: RepromaterialByPartnersReport) =>
        a.date < b.date ? 1 : -1
    );
    return copyOfRepromaterialByPartnersData;
  };

  const onSelectedPartnerChange = (property: string, value: string) => {
    setSelectedPartner(value);
  };

  const onDateFromChange = (property: string, value: Date | null) => {
    setDateFrom(value as Date);
  };

  const onDateToChange = (property: string, value: Date | null) => {
    setDateTo(value as Date);
  };

  const renderRepromaterialHeader = () => {
    return (
      <div className="repromaterial-header">
        <span>{t("REPROMATERIJAL")}</span>
      </div>
    );
  };

  const formatDate = (rowData: any) => {
    return Utility.formatDate(new Date(rowData.date));
  };

  const productAmount = (rowData: any) => {
    return !Number.isInteger(rowData.amount)
      ? rowData.amount.toFixed(3)
      : rowData.amount.toFixed(0);
  };

  const {
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
  });

  return (
    <PageLayout>
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-file-text header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
                transform: "translate(0, 5px)",
              }}
            ></i>
            <span className="header-title">
              {t("Repromaterijal isporučen partneru")}
            </span>
            <DropdownInput
              id="selectedPartner"
              value={selectedPartner}
              onValueChange={onSelectedPartnerChange}
              options={partnersApi}
              filter={true}
              optionValue="id"
              optionLabel="name"
              labelText=""
              placeholderText={t("Partner")}
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("od")}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("do")}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (downloadPdfDocument.current) {
                  // @ts-ignore
                  downloadPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (printPdfDocument.current) {
                  // @ts-ignore
                  printPdfDocument.current();
                }
              }}
            />
          </div>
        </div>
        <div className="table-body">
          {processing ? (
            <ProgressSpinner
              style={{ width: "100px", height: "100px", color: "green" }}
              strokeWidth="4"
              animationDuration=".8s"
            />
          ) : (
            <DataTable
              header={renderRepromaterialHeader}
              value={repromaterialByPartnersData}
              showGridlines
              stripedRows
            >
              <Column body={formatDate} header={t("Datum")} />
              <Column field="warehouseName" header={t("Magacin")} />
              <Column field="documentNumber" header={t("Br. otpremnice")} />
              <Column field="productName" header={t("Roba")} />
              <Column body={productAmount} header={t("Količina")} />
            </DataTable>
          )}
        </div>
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t("Isporučen repromaterijal za ")}${
              partnersApi.find((item) => item.id === selectedPartner)?.name ??
              ""
            }_${Utility.formatDate(new Date())}.pdf`}
          />
        </div>
      </div>
    </PageLayout>
  );
};
